import { lazy, Suspense, useEffect, useState } from 'react';
import { TeeLoader } from '@/components/ui/tee-loader';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useMatch,
  useNavigate,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { Toaster } from 'react-hot-toast';
import LoginPage from './pages/LoginPage';
import LoginPageLegacy from './pages/LoginPageLegacy';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import MainLayout from './layouts/MainLayout';
import { IntakeEntryPoint } from './components/intake/IntakeEntryPoint';
import ProfilePage from './pages/profile/ProfilePage';
import { useAuth } from './hooks/useAuth';
import { useRefreshCachedProfile } from './hooks/useRefreshCachedProfile';
import { NavigationProvider } from './contexts/NavigationContext';
import { UserProvider } from './contexts/UserContext';
import { AssistantProvider } from './contexts/AssistantContext';
import { TopBar } from './components/ui/top-bar';
import { IssueNoticeBanner } from '@/components/issue-notices/IssueNoticeBanner';
import { IssueAlertCards } from '@/components/issue-notices/IssueAlertCards';
import { ModuleTopBar } from '@/components/ui/module-top-bar/ModuleTopBar';
import { AssistantHost } from './components/assistant/AssistantHost';
import { AssistantToggle } from './components/assistant/AssistantToggle';
import { MobileNavSheet } from './components/ui/module-top-bar/MobileNavSheet';
import { MobileTabBar } from './components/ui/mobile-tab-bar';
import { useModuleConfig } from './hooks/useModuleConfig';
// Feature-flag helper for the Add Member route (from main). Plain function —
// nothing to code-split, so it stays a static import.
import { isAddMemberEnabled } from '@/config/featureFlags';
import { CartProvider } from './context/CartContext';
import PulseLayout from './pages/pulse/PulseLayout';
import { isCoachRole, COACH_LANDING_ROUTE } from '@/utils/roleRouting';

// Route-level code splitting. Each page below becomes its own chunk, fetched
// on first navigation rather than shipped in the initial bundle — the whole
// admin portal in one file is a real cost now that phones are a target.
// Login, the OAuth callback and the layouts stay eager: they are needed on
// first paint or on every route, so deferring them would only add a round trip.
const LauncherPage = lazy(() => import('./pages/LauncherPage'));
const DemoReportPage = lazy(() => import('./pages/performance/report/DemoReportPage'));
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const UsersFormPage = lazy(() => import('./pages/users/UserFormPage').then((m) => ({ default: m.UsersFormPage })));
const BookingTypePage = lazy(() => import('./pages/booking-types/BookingTypePage'));
const BookingTypeAddPage = lazy(() => import('./pages/booking-types/BookingTypeAddPage'));
const BookingTypeEditPage = lazy(() => import('./pages/booking-types/BookingTypeEditPage'));
const PlanPage = lazy(() => import('./pages/plans/PlanPage'));
const PlanAddPage = lazy(() => import('./pages/plans/PlanAddPage'));
const PlanEditPage = lazy(() => import('./pages/plans/PlanEditPage'));
const PlanDetailsPage = lazy(() => import('./pages/plans/PlanDetailsPage'));
const FranchisesPage = lazy(() => import('./pages/franchises/FranchisesPage'));
const FranchiseDetailsPage = lazy(() => import('./pages/franchises/FranchiseDetailsPage').then((m) => ({ default: m.FranchiseDetailsPage })));
const LocationsPage = lazy(() => import('./pages/locations/LocationsPage'));
const LocationsAddPage = lazy(() => import('./pages/locations/LocationsAddPage'));
const LocationDetailsPage = lazy(() => import('./pages/locations/LocationDetailsPage'));
const LocationSyncFailuresPage = lazy(() => import('./pages/locations/LocationSyncFailuresPage'));
const LocationsSnapshotPage = lazy(() => import('./pages/settings/LocationsSnapshotPage'));
const GhlIntegrationErrorLogPage = lazy(() => import('./pages/settings/GhlIntegrationErrorLogPage'));
const CorporateAccountsPage = lazy(() => import('./pages/corporate-accounts/CorporateAccountsPage'));
const CorporateAccountAddPage = lazy(() => import('./pages/corporate-accounts/CorporateAccountAddPage'));
const CorporateAccountDetailsPage = lazy(() => import('./pages/corporate-accounts/CorporateAccountDetailsPage'));
const MembersPage = lazy(() => import('./pages/members/MembersPage'));
const AddMemberPage = lazy(() => import('./pages/members/AddMemberPage'));
const CoachesPage = lazy(() => import('./pages/coaches/CoachesPage'));
const CoachCohortPage = lazy(() => import('./pages/coaches/CoachCohortPage'));
const MembersOverviewPage = lazy(() => import('./pages/members/MembersOverviewPage'));
const MemberDetailsPage = lazy(() => import('./pages/members/MemberDetailsPage'));
const MemberSyncFailuresPage = lazy(() => import('./pages/members/MemberSyncFailuresPage'));
const MemberReportsPage = lazy(() => import('./pages/members/MemberReportsPage'));
const PushNotificationsPage = lazy(() => import('./pages/push-notifications/PushNotificationsPage'));
const PushNotificationFormPage = lazy(() => import('./pages/push-notifications/PushNotificationFormPage'));
const PushNotificationDetailPage = lazy(() => import('./pages/push-notifications/PushNotificationDetailPage'));
const PerformancePage = lazy(() => import('./pages/performance/PerformancePage'));
const PlayerAssessmentPage = lazy(() => import('./pages/performance/PlayerAssessmentPage'));
const AssessmentReportPage = lazy(() => import('./pages/performance/AssessmentReportPage'));
const BoxAssessmentWizardPage = lazy(() => import('./pages/wizard/BoxAssessmentWizardPage'));
// Mock preview (direct URL only, not in nav): Goal Cascade goal-setting screen.
const GoalSettingMockPage = lazy(() => import('./pages/performance/GoalSettingMockPage'));
const EquipmentPage = lazy(() => import('./pages/equipment/EquipmentPage'));
const EquipmentDetailPage = lazy(() => import('./pages/equipment/EquipmentDetailPage'));
const SubmitOrdersPage = lazy(() => import('./pages/club-sales/SubmitOrdersPage'));
const ApproveOrdersPage = lazy(() => import('./pages/club-sales/ApproveOrdersPage'));
const OrdersListPage = lazy(() => import('./pages/club-sales/OrdersListPage'));
const CreateOrderPage = lazy(() => import('./pages/club-sales/CreateOrderPage'));
const EditOrderPage = lazy(() => import('./pages/club-sales/CreateOrderPage').then((m) => ({ default: m.EditOrderPage })));
const OrderDetailPage = lazy(() => import('./pages/club-sales/OrderDetailPage'));
const ClubSalesReportsPage = lazy(() => import('./pages/club-sales/ClubSalesReportsPage'));
const InboxPage = lazy(() => import('./pages/inbox/InboxPage'));
const BannersPage = lazy(() => import('./pages/banners/BannersPage'));
const IssueCentrePage = lazy(() => import('./pages/issues/IssueCentrePage'));
const IssueNoticeFormPage = lazy(() => import('./pages/issues/IssueNoticeFormPage'));
const IssueNoticeDetailPage = lazy(() => import('./pages/issues/IssueNoticeDetailPage'));
const IssueSettingsPage = lazy(() => import('./pages/issues/IssueSettingsPage'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const CalendarReconciliationPage = lazy(() => import('./pages/calendar/CalendarReconciliationPage'));
const WaiversPage = lazy(() => import('./pages/waivers/WaiversPage'));
const WaiverDetailPage = lazy(() => import('./pages/waivers/WaiverDetailPage'));
const WaiverSignaturesPage = lazy(() => import('./pages/waivers/WaiverSignaturesPage'));
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage'));
const LeadDetailPage = lazy(() => import('./pages/leads/LeadDetailPage'));
const PulseDashboardPage = lazy(() => import('./pages/pulse/PulseDashboardPage'));
const MyPitchNumbersPage = lazy(() => import('./pages/pulse/MyPitchNumbersPage'));
const BayUtilizationPage = lazy(() => import('./pages/pulse/utilization/BayUtilizationPage'));
const PulseRevenuePage = lazy(() => import('./pages/pulse/revenue/PulseRevenuePage'));
const SalesAnalyticsPage = lazy(() => import('./pages/pulse/sales-analytics/SalesAnalyticsPage'));
const PulseMembersPage = lazy(() => import('./pages/pulse/members/PulseMembersPage'));
const TransactionsPage = lazy(() => import('./pages/transactions/TransactionsPage'));
const PulseMarketingPage = lazy(() => import('./pages/pulse/marketing/PulseMarketingPage'));
const AssistantPage = lazy(() => import('./pages/support-assistant/AssistantPage'));
const SupportTicketsPage = lazy(() => import('./pages/support-tickets/SupportTicketsPage'));
const SupportAuditPage = lazy(() => import('./pages/support-audit/SupportAuditPage'));
const SupportUsagePage = lazy(() => import('./pages/support-usage/SupportUsagePage'));
const TeeSheetPage = lazy(() => import('./pages/pulse/tee-sheet/TeeSheetPage'));
const CaddyPage = lazy(() => import('./pages/pulse/caddy/CaddyPage'));
const PulseReportsPage = lazy(() => import('./pages/pulse/reports/PulseReportsPage'));
const OtpDisplayPage = lazy(() => import('./pages/OtpDisplayPage'));
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const FeatureBoardPage = lazy(() => import('./pages/linear/FeatureBoardPage'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));
const FaqManagePage = lazy(() => import('./pages/help/FaqManagePage'));
const FaqSectionDetailPage = lazy(() => import('./pages/help/FaqSectionDetailPage'));
import ContentStudioPage from './pages/content-library/ContentStudioPage';
import PlaylistBuilderPage from './pages/content-library/PlaylistBuilderPage';
import ResourceFinderPage from './pages/resource-finder/ResourceFinderPage';
import OwnerPlaybookPage from './pages/game-plan/OwnerPlaybookPage';
import CurrentWeekPage from './pages/game-plan/CurrentWeekPage';
import GoalBoardPage from './pages/game-plan/GoalBoardPage';
import OverviewPage from './pages/game-plan/OverviewPage';
import { rememberGopherDeepLink } from '@/utils/gopherLaunch';

function RedirectToLogin() {
  const { search } = useLocation();
  // Keep the ?gopher=open deep link alive across the login redirect.
  useEffect(() => {
    rememberGopherDeepLink(search);
  }, [search]);
  return <Navigate to="/login" replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  if (isAuthenticated) return <>{children}</>;
  return <RedirectToLogin />;
}

/**
 * Renders in place of the index route so a coach who visits `/` directly
 * (bookmark, or funnelled here by the `*` catch-all) lands on their cohort
 * instead of the generic launcher — the login-time redirect alone only
 * covers the moment of authenticating, not every later visit to `/`. Shares
 * ProtectedRoute/AppRoutes' loading treatment so the launcher's module tiles
 * are never painted for a coach mid-resolution.
 */
function LauncherIndexRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  if (isCoachRole(user)) {
    return <Navigate to={COACH_LANDING_ROUTE} replace />;
  }

  return <LauncherPage />;
}

/**
 * AppShell: thin layout wrapper.
 * Renders the legacy TopBar only on the launcher (pathname === '/').
 * On module pages it renders the ModuleTopBar; MainLayout supplies the sub-nav and page body.
 */
function AppShell() {
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const {
    currentModule,
    allModules,
    moduleTree,
    mobileTabs,
    hasInbox,
    inboxCount,
    hasHqQueue,
    hqQueueCount,
    hasIssueNotices,
    loading,
  } = useModuleConfig();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLauncher = pathname === '/';
  // The wizard and the assessment report render chrome-free (no top bar, no assistant).
  // Call both hooks unconditionally — `||` would short-circuit and change the hook count.
  const isWizardAssessment = !!useMatch('/wizard/:customerType/:customerId/assessment');
  const isPerformanceReport = !!useMatch('/performance/report/:customerType/:customerId');
  const isChromeFree = isWizardAssessment || isPerformanceReport;
  const isModuleRoute = !isLauncher && !isChromeFree;
  const hasChrome = isLauncher || isModuleRoute;
  const { isAuthenticated } = useAuth();
  // Re-signs the cached profile photo URL, which expires, and picks up profile
  // edits made in another tab or session.
  useRefreshCachedProfile(isAuthenticated);
  return (
    <>
      {isLauncher && (
        <TopBar
          className="flex-shrink-0 relative z-[60]"
          onOpenMobileNav={() => setMobileNavOpen(true)}
          showInbox={hasInbox}
          inboxCount={inboxCount}
          inboxLabel={t('navigation.inbox')}
          onInboxClick={() => navigate('/inbox')}
          showHqQueue={hasHqQueue}
          hqQueueCount={hqQueueCount}
          hqQueueLabel={t('navigation.support_tickets')}
          onHqQueueClick={() => navigate('/support/tickets?status=open')}
          assistantSlot={
            <>
              <AssistantToggle />
            </>
          }
        />
      )}
      {isModuleRoute && (
        <ModuleTopBar
          currentModule={currentModule}
          allModules={allModules}
          onBackToLauncher={() => navigate('/')}
          loading={loading}
          onOpenMobileNav={() => setMobileNavOpen(true)}
          showInbox={hasInbox}
          inboxCount={inboxCount}
          inboxLabel={t('navigation.inbox')}
          showHqQueue={hasHqQueue}
          hqQueueCount={hqQueueCount}
          hqQueueLabel={t('navigation.support_tickets')}
          assistantSlot={
            <>
              <AssistantToggle />
            </>
          }
        />
      )}
      {hasChrome && hasIssueNotices && <IssueNoticeBanner />}
      {hasChrome && hasIssueNotices && <IssueAlertCards />}
      {hasChrome && (
        <MobileNavSheet
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          moduleTree={moduleTree}
          currentModuleKey={currentModule?.key ?? null}
        />
      )}
      {/* The assistant host lives here, at one stable position for the launcher
          and every module route, so navigating between them never remounts it
          (a remount would abort an in-flight stream). Chrome-free routes (wizard,
          report) render no host. */}
      <div className="relative flex flex-1 overflow-hidden">
        <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
          <Outlet />
        </div>
        {hasChrome && <AssistantHost />}
      </div>
      {/* The launcher renders outside MainLayout, so it needs its own tab bar.
          Module routes get theirs from MainLayout — rendering it here too would
          double it up. None of the tabs is active on the launcher itself, which
          is correct: it is the root, not one of the destinations. */}
      {isLauncher && <MobileTabBar tabs={mobileTabs} />}
      {/* Intake Panel entry point (408 D-01/D-02). Lives here so it appears on
          the launcher and every module route, but never on login/public pages
          or the chrome-free wizard/report routes (kiosk-facing full-screen flows). */}
      {hasChrome && <IntakeEntryPoint />}
    </>
  );
}

function AppRoutes() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  }

  return (
    <div className="bg-[var(--background-page-body)] flex flex-col h-screen w-screen">
      {/* Lazy routes need a Suspense boundary. TeeLoader keeps the chunk
          fetch visually identical to the in-page loading states. */}
      <Suspense fallback={<TeeLoader fullScreen />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login-v2" element={<LoginPageLegacy />} />
        <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
        <Route path="/otp-display" element={<OtpDisplayPage />} />

        {/* Standalone, public demo of the assessment report (storytelling) */}
        <Route path="/demo/pdp/report" element={<DemoReportPage />} />
        <Route path="/demo/pdp/report/:slug" element={<DemoReportPage />} />

        {/* Shared auth + context wrapper — pathless */}
        <Route
          element={
            <ProtectedRoute>
              <NavigationProvider>
                <UserProvider>
                  <AssistantProvider>
                  {/* No width cap below `sm`: the previous max-w-[390px] was
                      letterboxing the whole app on any phone wider than 390px
                      (a 14 Pro Max is 430), which is what produced the dead
                      margins down both sides of every screen. */}
                  <div className="flex flex-col flex-1 overflow-hidden mx-auto w-full sm:max-w-[834px] lg:max-w-[1440px] h-full">
                    <AppShell />
                  </div>
                  </AssistantProvider>
                </UserProvider>
              </NavigationProvider>
            </ProtectedRoute>
          }
        >
          {/* Index: Launcher — renders without MainLayout (AppShell adds TopBar).
              A coach is redirected to /my-cohort instead of the launcher. */}
          <Route index element={<LauncherIndexRoute />} />

          {/* The wizard is a full-page experience with its own fixed header — registered outside
              MainLayout (so it gets no ModuleTopBar/ModuleSubNav) but still inside this
              authenticated group, inheriting ProtectedRoute's auth guard (T-365-44). AppShell only
              adds its own TopBar on the launcher route, so nothing else leaks in here either. */}
          <Route
            path="wizard/:customerType/:customerId/assessment"
            element={<BoxAssessmentWizardPage />}
          />

          {/* Full-screen assessment "reveal" report (368 D-13) — registered outside
              MainLayout so it renders chrome-free (the flip-the-iPad surface), like the
              wizard above. Still inside the authenticated group (ProtectedRoute). */}
          <Route
            path="performance/report/:customerType/:customerId"
            element={<AssessmentReportPage />}
          />

          {/* Module shell — MainLayout wraps all module routes */}
          <Route element={<MainLayout />}>
            {/* Self-service profile — no permission gate: every authenticated
                user may edit their own record, and the API authorises on the
                token rather than a target id. */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/create" element={<UsersFormPage />} />
            <Route path="users/:id/edit" element={<UsersFormPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="booking-types" element={<BookingTypePage />} />
            <Route
              path="booking-types/create"
              element={<BookingTypeAddPage />}
            />
            <Route
              path="booking-types/:id/edit"
              element={<BookingTypeEditPage />}
            />
            <Route path="plans" element={<PlanPage />} />
            <Route path="plans/create" element={<PlanAddPage />} />
            <Route path="plans/:id" element={<PlanDetailsPage />} />
            <Route path="plans/:id/edit" element={<PlanEditPage />} />
            <Route path="franchises" element={<FranchisesPage />} />
            <Route
              path="franchises/:id/view"
              element={<FranchiseDetailsPage />}
            />
            <Route path="locations" element={<LocationsPage />} />
            <Route path="locations/create" element={<LocationsAddPage />} />
            <Route
              path="locations/sync-failures"
              element={<LocationSyncFailuresPage />}
            />
            <Route
              path="locations/:id/view"
              element={<LocationDetailsPage />}
            />
            <Route
              path="corporate-accounts"
              element={<CorporateAccountsPage />}
            />
            <Route
              path="corporate-accounts/create"
              element={<CorporateAccountAddPage />}
            />
            <Route
              path="corporate-accounts/:id/view"
              element={<CorporateAccountDetailsPage />}
            />
            <Route path="members" element={<MembersPage />} />
            <Route path="members/overview" element={<MembersOverviewPage />} />
            <Route
              path="members/sync-failures"
              element={<MemberSyncFailuresPage />}
            />
            <Route path="members/reports" element={<MemberReportsPage />} />
            <Route
              path="members/notifications"
              element={<PushNotificationsPage />}
            />
            <Route
              path="members/notifications/create"
              element={<PushNotificationFormPage />}
            />
            <Route
              path="members/notifications/:id"
              element={<PushNotificationDetailPage />}
            />
            <Route
              path="members/notifications/:id/edit"
              element={<PushNotificationFormPage />}
            />
            <Route
              path="members/add"
              element={
                isAddMemberEnabled() ? (
                  <AddMemberPage />
                ) : (
                  <Navigate to="/members" replace />
                )
              }
            />
            <Route path="members/:id/view" element={<MemberDetailsPage />} />
            <Route path="coaches" element={<CoachesPage />} />
            <Route path="coaches/:coachId/cohort" element={<CoachCohortPage />} />
            <Route path="my-cohort" element={<CoachCohortPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:id/view" element={<LeadDetailPage />} />
            <Route path="performance" element={<PerformancePage />} />
            <Route
              path="performance/:memberId/assessment"
              element={<PlayerAssessmentPage />}
            />
            <Route path="performance/goals-mock" element={<GoalSettingMockPage />} />
            <Route
              path="club-sales"
              element={<Navigate to="/club-sales/reports" replace />}
            />
            {/* Reports view is now the Sales Dashboard; keep the old path as a redirect. */}
            <Route
              path="club-sales/dashboard"
              element={<Navigate to="/club-sales/reports" replace />}
            />
            <Route path="club-sales/equipment" element={<EquipmentPage />} />
            <Route
              path="club-sales/equipment/:id/view"
              element={<EquipmentDetailPage />}
            />
            <Route
              path="club-sales/submit-orders"
              element={<SubmitOrdersPage />}
            />
            <Route
              path="club-sales/approve-orders"
              element={<ApproveOrdersPage />}
            />
            <Route
              path="club-sales/reports"
              element={<ClubSalesReportsPage />}
            />
            <Route
              element={
                <CartProvider>
                  <Outlet />
                </CartProvider>
              }
            >
              <Route path="club-sales/orders" element={<OrdersListPage />} />
              <Route
                path="club-sales/orders/create"
                element={<CreateOrderPage />}
              />
              <Route
                path="club-sales/orders/:id"
                element={<OrderDetailPage />}
              />
              <Route
                path="club-sales/orders/:id/edit"
                element={<EditOrderPage />}
              />
              <Route
                path="club-sales/approve-orders/:id"
                element={<OrderDetailPage />}
              />
            </Route>
            <Route path="inbox" element={<InboxPage />} />
            <Route
              path="equipment"
              element={<Navigate to="/club-sales/equipment" replace />}
            />
            <Route
              path="settings/locations"
              element={<LocationsSnapshotPage />}
            />
            <Route path="settings/banners" element={<BannersPage />} />
            <Route path="issues" element={<IssueCentrePage />} />
            <Route path="issues/new" element={<IssueNoticeFormPage />} />
            <Route path="issues/settings" element={<IssueSettingsPage />} />
            <Route path="issues/:id" element={<IssueNoticeDetailPage />} />
            <Route path="issues/:id/edit" element={<IssueNoticeFormPage />} />
            <Route
              path="settings/ghl-integration-errors"
              element={<GhlIntegrationErrorLogPage />}
            />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="calendar/reconcile" element={<CalendarReconciliationPage />} />
            <Route path="settings/waivers" element={<WaiversPage />} />
            <Route path="settings/waivers/new" element={<WaiverDetailPage />} />
            <Route path="settings/waivers/:id" element={<WaiverDetailPage />} />
            <Route
              path="settings/waivers/:id/signatures"
              element={<WaiverSignaturesPage />}
            />
            <Route path="pulse" element={<PulseLayout />}>
              {/* Dashboard hidden from Pulse nav (TEE-372); landing → Revenue */}
              <Route index element={<Navigate to="/pulse/revenue" replace />} />
              <Route path="dashboard" element={<PulseDashboardPage />} />
              <Route path="utilization" element={<BayUtilizationPage />} />
              <Route path="revenue" element={<PulseRevenuePage />} />
              <Route path="sales-analytics" element={<SalesAnalyticsPage />} />
              {/* Box-pro self-view: own location-scoped pitch numbers. Auth-only
                  data (/pitch-analytics/board); nav node gated on PULSE. */}
              <Route path="my-pitch-numbers" element={<MyPitchNumbersPage />} />
              <Route path="members" element={<PulseMembersPage />} />
              <Route path="marketing" element={<PulseMarketingPage />} />
              <Route path="reports" element={<PulseReportsPage />} />
            </Route>
            {/* Sits alongside Pulse in the nav but outside PulseLayout — it is a
                record list, not a dashboard, and carries its own filters. */}
            <Route path="transactions" element={<TransactionsPage />} />
            <Route path="caddy" element={<CaddyPage />} />
            <Route path="tee-sheet" element={<TeeSheetPage />} />
            <Route path="resources" element={<ResourcesPage />} />
            <Route path="release-notes" element={<ReleaseNotesPage />} />
            <Route path="feature-board" element={<FeatureBoardPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="support/assistant" element={<AssistantPage />} />
            <Route path="support/tickets" element={<SupportTicketsPage />} />
            <Route path="support/audit" element={<SupportAuditPage />} />
            <Route path="support/usage" element={<SupportUsagePage />} />
            <Route path="faq/manage" element={<FaqManagePage />} />
            <Route path="faq/manage/:sectionId" element={<FaqSectionDetailPage />} />
            <Route path="content-library/studio" element={<ContentStudioPage />} />
            <Route
              path="content-library/studio/playlists/:id"
              element={<PlaylistBuilderPage />}
            />
            <Route path="content-library/resource-finder" element={<ResourceFinderPage />} />
            <Route path="game-plan/playbook" element={<OwnerPlaybookPage />} />
            <Route path="game-plan/current-week" element={<CurrentWeekPage />} />
            <Route path="game-plan/goal-board" element={<GoalBoardPage />} />
            <Route path="game-plan/overview" element={<OverviewPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </div>
  );
}

export default function App() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!googleClientId) {
    console.warn(
      '[App] VITE_GOOGLE_CLIENT_ID is not set. Google OAuth login will fail. Check your .env file.'
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId ?? ''}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AppRoutes />
          <Toaster position="top-right" />
        </Router>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}
